import { useRef } from 'react';
import { DateTime as DT } from 'luxon';
import type { ProductType } from '@econtractor/types';
import { useAuth } from 'context/AuthContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import CopyTextWrapper from 'components/others/CopyTextWrapper';
import PriceAndPriceSale from 'components/others/PriceAndPriceSale';
import ProductTitle from './ProductRowItems/ProductTitle';
import ProductTableRowActions from './ProductTableRowActions';
import { useProduct } from './context/ProductProvider';

interface ProductTableRowProps {
    product: ProductType;
    fetchProducts: (force?: boolean) => Promise<unknown>;
}

const ProductTableRow = ({ product, fetchProducts }: ProductTableRowProps) => {
    const { auth } = useAuth();
    const dt = DT.fromMillis(product?.prodUpdated || 0).toFormat('dd.MM.yyyy');
    const { openProductCard, productCheck, setProductCheck, showCategory, showId, showArticle, showUpdated } = useProduct();
    const refCheck = useRef<HTMLInputElement>(null);

    return (
        <tr>
            <td className="check">
                <input
                    className="form-check-input"
                    disabled={!hasUserAccess(auth?.user, 'prodEdit')}
                    ref={refCheck}
                    id={`check-${product.ID}`}
                    checked={productCheck?.includes(product.ID)}
                    onChange={(e) => {
                        setProductCheck((prev) => {
                            if (e.target.checked) {
                                return [...prev, product.ID];
                            }
                            return prev.filter((pId) => pId !== product.ID);
                        });
                    }}
                    type="checkbox"
                />
            </td>
            <td className="name">
                <ProductTitle
                    product={product}
                    fetchProducts={fetchProducts}
                    onClick={() => {
                        openProductCard(product.ID);
                    }}
                    onPhotoClick={() => {
                        openProductCard(product.ID, 'photo');
                    }}
                />
            </td>
            {showCategory ? (
                <td className="vendor">
                    <h6 className="mb-1 text-break text-truncate" title={product.categoryName || ''}>
                        {product.categoryName || '-'}
                    </h6>
                </td>
            ) : null}
            {showId ? (
                <td className="id">
                    <code>{product.ID}</code>
                </td>
            ) : null}
            {showArticle ? (
                <td className="article text-truncate">
                    <CopyTextWrapper value={product.prodArticle || ''}>
                        <span title={product.prodArticle}>{product.prodArticle}</span>
                    </CopyTextWrapper>
                </td>
            ) : null}
            {showUpdated ? <td className="updated">{dt}</td> : null}
            <td className="price text-truncate">
                <PriceAndPriceSale
                    price={product?.prodDefPrice}
                    priceSale={product?.prodDefPriceSale}
                />
            </td>
            {hasUserAccess(auth?.user, 'prodEdit') && (
                <td className="action">
                    <ProductTableRowActions product={product} fetchProducts={fetchProducts} />
                </td>
            )}
        </tr>
    );
};

export default ProductTableRow;
